import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { LockIcon } from 'lucide-react';
import { AdminBarChart, DistributionList } from '../../components/admin/AdminChart';
import { AdminMetricCard, AdminPageTitle, AdminTabs, ConfirmationModal, Panel, StatusBadge } from '../../components/admin/AdminPrimitives';
import { DataTable, type Column } from '../../components/admin/DataTable';
import { Button } from '../../components/ui/Button';
import { adminBadges, challenges, levelDistribution, progressionRules, recordAudit, streakBuckets, xpDistribution, type AdminBadge } from '../../data/admin';
import { useLocale } from '../../contexts/LocaleContext';
import { ConfigParamsPanel } from '../../components/admin/ConfigParamsPanel';
import { FixedRulesPanel } from '../../components/admin/FixedRulesPanel';
import { engagementAdminSections, type EngagementAdminSection } from '../../data/engagementAdmin';
import { hasCapability, prototypeAdminRole } from '../../data/adminOperations';
const tabs = [{
  id: 'xp',
  label: 'Bee'
}, {
  id: 'niveis',
  label: 'Níveis'
}, {
  id: 'sequencias',
  label: 'Sequências'
}, {
  id: 'medalhas',
  label: 'Medalhas'
}, {
  id: 'desafios',
  label: 'Desafios'
}, {
  id: 'protecao',
  label: 'Proteção de Sequência'
}, {
  id: 'colmeia',
  label: 'Minha Colmeia'
}, {
  id: 'bau',
  label: 'Baú da Colmeia'
}, {
  id: 'liga',
  label: 'Liga'
}];
const engagementTabs: Record<string, EngagementAdminSection> = {
  protecao: 'protection',
  colmeia: 'hive',
  bau: 'chest',
  liga: 'league'
};
export function AdminGamification() {
  const {
    tab: param
  } = useParams();
  const {
    tl
  } = useLocale();
  const navigate = useNavigate();
  const tab = tabs.some(item => item.id === param) ? param as string : 'xp';
  const [editing, setEditing] = useState<AdminBadge | null>(null);
  const badgeColumns: Column<AdminBadge>[] = [{
    id: 'name',
    header: 'Medalha',
    sortValue: row => row.name,
    cell: row => <span className="text-[13px] font-medium text-deep" data-id="element-1414" data-mp="246vdbefvzrw4">{row.name}</span>
  }, {
    id: 'rule',
    header: 'Regra de qualificação',
    width: '34%',
    cell: row => <span className="text-[12.5px] text-muted" data-id="element-1415" data-mp="1ug91lvbajlip">{tl(row.rule)}</span>
  }, {
    id: 'unlocked',
    header: 'Desbloqueios',
    align: 'right',
    sortValue: row => Number(row.unlocked.replace(/\./g, '')),
    cell: row => <span className="bee-num font-bold text-deep" data-id="element-1416" data-mp="1lglz166ts9o2">{row.unlocked}</span>
  }, {
    id: 'status',
    header: 'Status',
    cell: row => <StatusBadge status={row.status} label={row.status === 'active' ? 'Ativa' : 'Rascunho'} data-id="element-1417" data-mp="1bho9q11ksc40" />
  }, {
    id: 'created',
    header: 'Criada em',
    sortValue: row => row.created,
    cell: row => <span className="bee-num text-muted" data-id="element-1418" data-mp="35vxokmojokji">{row.created}</span>
  }];
  return <div className="space-y-4" data-id="element-1419" data-mp="30wukb4ge2icl">
      <AdminPageTitle title="Gamificação" subtitle="Distribuição de Bee, níveis, sequências, medalhas e recursos de engajamento. Regras fixas do produto são somente leitura; parâmetros configuráveis exigem permissão." data-id="element-1420" data-mp="30xw6a3yy0p93" />

      <AdminTabs tabs={tabs} active={tab} onChange={next => navigate(`/admin/gamificacao/${next}`)} data-id="element-1421" data-mp="3dcu87gl1x4ky" />

      {tab === 'xp' && <>
          <section className="grid grid-cols-2 gap-3 lg:grid-cols-4" data-id="element-1422" data-mp="3n3yl8ar62fd5">
            <AdminMetricCard label="Bee médio por usuário" value="74,2" delta={0.9} accent data-id="element-1423" data-mp="2qf0owo3rq9dv" />
            <AdminMetricCard label="Usuários em 100 Bee hoje" value="31.208" delta={6.7} data-id="element-1424" data-mp="2ugcl5ch5dsv1" />
            <AdminMetricCard label="Acima do gate de 50 Bee" value="81.040" delta={4.2} data-id="element-1425" data-mp="2ui5oahsjxuya" />
            <AdminMetricCard label="Bônus de foto usados" value="12.744" delta={2.8} data-id="element-1426" data-mp="2zhiad0blmxzt" />
          </section>

          <AdminBarChart title="Distribuição de Bee no dia" question="Quantos usuários param antes do gate de 50 Bee?" data={xpDistribution} xKey="range" series={[{
        id: 'users',
        name: 'Usuários'
      }]} data-id="element-1427" data-mp="34c5ukmzkl68l" />

          <Panel title="Regras de progressão" hint="Configuração vigente, em modo somente leitura." data-id="element-1428" data-mp="1slhw306lnnlw">
            <ul className="space-y-2" data-id="element-1429" data-mp="1nnzkt3540waq">
              {progressionRules.map(rule => <li key={rule.id} className="flex items-center justify-between gap-3 border-b border-sand-line pb-2 last:border-b-0 last:pb-0" data-id="element-1430" data-mp="21nhcprdpcpob">
                  <span className="text-[12.5px] text-deep" data-id="element-1431" data-mp="2bij1un7z9rqw">{tl(rule.label)}</span>
                  <span className="flex items-center gap-2" data-id="element-1432" data-mp="26jy4cvk41m65">
                    <span className="bee-num text-[12.5px] font-bold text-deep" data-id="element-1433" data-mp="2kc5vo7smkkxg">
                      {rule.value}
                    </span>
                    <LockIcon size={13} className="text-muted" aria-hidden="true" data-id="element-1434" data-mp="2gc3qhonol9df" />
                  </span>
                </li>)}
            </ul>
            <p className="mt-3 text-[11.5px] leading-snug text-muted" data-id="element-1435" data-mp="2pbqw6mng11ca">
              A base de 100 Bee, o gate de 50 Bee e o bônus de +10 Bee fora da base são regras
              críticas do produto. A edição exige aprovação de Super Admin e não é exposta
              aqui.
            </p>
          </Panel>
        </>}

      {tab === 'niveis' && <div className="grid grid-cols-1 gap-3 xl:grid-cols-2" data-id="element-1436" data-mp="2yyuue7f1yu2s">
          <AdminBarChart title="Usuários por nível" question="A base está avançando de nível ou concentrada no início?" data={levelDistribution} xKey="level" series={[{
        id: 'users',
        name: 'Usuários'
      }]} data-id="element-1437" data-mp="33x62odpp8wiw" />
          <Panel title="Proporção por nível" data-id="element-1438" data-mp="2g8v8tuif7x44">
            <DistributionList items={levelDistribution.map(level => ({
          label: level.level,
          value: level.users,
          secondary: level.users.toLocaleString('pt-BR')
        }))} data-id="element-1439" data-mp="2ga6drxnlrawt" />
          </Panel>
        </div>}

      {tab === 'sequencias' && <>
          <section className="grid grid-cols-2 gap-3 lg:grid-cols-4" data-id="element-1440" data-mp="hqt7rq3ag6j5">
            <AdminMetricCard label="Sequências ativas" value="31.208" delta={6.7} accent data-id="element-1441" data-mp="hsw4su2uyzxd" />
            <AdminMetricCard label="Média da sequência ativa" value="5,4 dias" delta={1.2} data-id="element-1442" data-mp="cyrvw5a18htn" />
            <AdminMetricCard label="Quebras ontem" value="2.917" delta={-3.1} data-id="element-1443" data-mp="81ja7upodvnn" />
            <AdminMetricCard label="Acima de 30 dias" value="228" delta={9.4} data-id="element-1444" data-mp="32yarxi8zg09" />
          </section>
          <AdminBarChart title="Sequências por faixa" question="Onde as sequências costumam quebrar?" data={streakBuckets} xKey="bucket" series={[{
        id: 'users',
        name: 'Usuários'
      }]} horizontal data-id="element-1445" data-mp="10om8qmucq6dw" />
        </>}

      {tab === 'medalhas' && <>
          <DataTable rows={adminBadges} columns={badgeColumns} rowKey={row => row.id} pageSize={10} actions={[{
        id: 'edit',
        label: 'Editar regra',
        onSelect: setEditing
      }]} data-id="element-1446" data-mp="mrz2ftgie3vb" />
          <Panel title="Edição de medalhas" data-id="element-1447" data-mp="1mznnq0cuo4rh">
            <p className="text-[12.5px] leading-relaxed text-muted" data-id="element-1448" data-mp="1qzej6hdbn92c">
              Alterar a regra de uma medalha muda a qualificação de toda a base. Por isso a
              edição exige confirmação explícita e fica registrada na auditoria com a regra
              anterior e a nova.
            </p>
          </Panel>
        </>}

      {tab === 'desafios' && <Panel title="Desafios" hint="Campanhas com período definido." data-id="element-1449" data-mp="25qojkfiobk6t">
          <ul className="space-y-2" data-id="element-1450" data-mp="2amc5mm1uzx83">
            {challenges.map(challenge => <li key={challenge.id} className="flex flex-wrap items-center justify-between gap-2 border-b border-sand-line pb-2 last:border-b-0 last:pb-0" data-id="element-1451" data-mp="2eqbkz1bekpzv">
                <span className="min-w-0" data-id="element-1452" data-mp="kcakyd449wy7">
                  <span className="block text-[13px] font-medium text-deep" data-id="element-1453" data-mp="kdlsmo0onoul">
                    {challenge.name}
                  </span>
                  <span className="bee-num block text-[11.5px] text-muted" data-id="element-1454" data-mp="2r72srr0avlio">
                    {challenge.period} · {challenge.participants} participantes
                  </span>
                </span>
                <StatusBadge status={challenge.status} label={challenge.status === 'active' ? 'Ativo' : 'Rascunho'} data-id="element-1455" data-mp="30xxkhawbfdbi" />
              </li>)}
          </ul>
          <Button variant="outline" className="mt-3" data-id="element-1456" data-mp="289rxsilpbe91">
            Novo desafio
          </Button>
        </Panel>}

      {engagementTabs[tab] && <div className="grid grid-cols-1 gap-3 xl:grid-cols-[1fr_1.4fr]" data-id="element-1457" data-mp="2i6wl4lra9frf">
          <FixedRulesPanel rules={engagementAdminSections[engagementTabs[tab]].fixedRules} data-id="element-1458" data-mp="2i53cj0x40lgs" />
          <ConfigParamsPanel key={tab} section={tl(engagementAdminSections[engagementTabs[tab]].title)} params={engagementAdminSections[engagementTabs[tab]].params} canEdit={hasCapability(prototypeAdminRole, 'engagement.manageConfig')} data-id="element-1459" data-mp="2da6681r7sq7l" />
        </div>}

      <ConfirmationModal open={editing !== null} title={`Editar a regra de ${editing?.name ?? ''}?`} body="A nova regra revalida a qualificação de toda a base, o que pode conceder ou remover a medalha de usuários já atendidos. A alteração fica registrada na auditoria." confirmLabel="Entendi, continuar" onConfirm={() => {
      if (editing) {
        recordAudit({
          action: {
            'pt-BR': 'Editou regra de medalha',
            'en-US': 'Edited badge rule'
          },
          entity: `badge:${editing.id}`,
          context: {
            'pt-BR': editing.name,
            'en-US': editing.name
          },
          before: tl(editing.rule),
          severity: 'sensitive'
        });
      }
      setEditing(null);
    }} onClose={() => setEditing(null)} data-id="element-1460" data-mp="1ahtto3o61h70" />
    </div>;
}